import { useMemo, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@tasama/i18n';
import {
  Dropdown,
  InputWithLabel,
  MobileInputField,
  Grid,
  OnboardingHeader,
} from '@tasama/ui';
import { getCountryOptions } from '@tasama/utils';
import {
  createYourDetailsSchema,
  yourDetailsDefaultValues,
  type YourDetailsFormValues,
} from '../schemas/your-details.schema';
import { useYourDetailsStore } from '../stores/your-details.store';
import type { YourDetailsStore } from '../stores/your-details.store';
import { useCompanyTypeFormData } from '../../company-type/stores/company-type-store';

const DEFAULT_COUNTRY_CODE = '+966';

export function YourDetailsPage() {
  const { t } = useTranslation('onboarding');

  const namePrefixes = [
    { value: 'Mr', label: t('common:personForm.prefixes.mr') },
    { value: 'Mrs', label: t('common:personForm.prefixes.mrs') },
    { value: 'Miss', label: t('common:personForm.prefixes.miss') },
    { value: 'Ms', label: t('common:personForm.prefixes.ms') },
    { value: 'Dr', label: t('common:personForm.prefixes.dr') },
  ];

  const title = t('yourDetails.title');
  const description = t('yourDetails.description');

  const { companyType = '', companyStatus = '' } = useCompanyTypeFormData();
  const countries = useMemo(() => getCountryOptions(), []);

  const schema = useMemo(
    () => createYourDetailsSchema(companyType, companyStatus, t),
    [companyType, companyStatus, t]
  );

  // Read persisted data — restores fields when navigating back to this step
  const storedFormData = useYourDetailsStore.getState().formData;

  const {
    watch,
    control,
    setValue,
    setError,
    trigger,
    formState: { errors, isValid },
  } = useForm<YourDetailsFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
    mode: 'onTouched',
    defaultValues: {
      ...yourDetailsDefaultValues,
      ...storedFormData,
    } as YourDetailsFormValues,
  });

  // Coordinator store sync — keeps use-step-validation and use-onboarding-payload working
  const setStoreValidity = useYourDetailsStore(
    (s: YourDetailsStore) => s.setValidity
  );
  const setStoreFormData = useYourDetailsStore(
    (s: YourDetailsStore) => s.setFormData
  );
  const pendingApiError = useYourDetailsStore(
    (s: YourDetailsStore) => s.pendingApiError
  );
  const clearApiError = useYourDetailsStore(
    (s: YourDetailsStore) => s.clearApiError
  );

  useEffect(() => {
    setStoreValidity(isValid);
  }, [isValid, setStoreValidity]);

  // Bridge API field errors (e.g. 409 email conflict) into RHF
  useEffect(() => {
    if (pendingApiError) {
      setError(pendingApiError.field as keyof YourDetailsFormValues, {
        type: 'server',
        message: pendingApiError.message,
      });
      clearApiError();
    }
  }, [pendingApiError, setError, clearApiError]);

  useEffect(() => {
    const subscription = watch((values) => {
      setStoreFormData(values as Partial<YourDetailsFormValues>);
    });
    return () => subscription.unsubscribe();
  }, [watch, setStoreFormData]);

  const formValues = useWatch({ control });

  const handleFieldChange = (
    field: keyof YourDetailsFormValues,
    value: string
  ) => {
    setValue(field, value as any, { shouldValidate: true, shouldTouch: true });

    // Keep computed phoneNumber in sync; re-validate the counterpart field
    if (field === 'country' || field === 'mobile') {
      const country = field === 'country' ? value : (formValues.country ?? '');
      const mobile = field === 'mobile' ? value : (formValues.mobile ?? '');
      const phoneNumber =
        country.trim() && mobile.trim() ? `${country}${mobile}` : '';
      setValue('phoneNumber', phoneNumber, { shouldValidate: false });

      if (field === 'country' && mobile) trigger('mobile');
    }
  };

  return (
    <div className="space-y-6">
      <OnboardingHeader
        size="lg"
        title={title}
        description={description}
        icon="User"
        testID="details-header"
      />
      <form className="space-y-6" noValidate>
        <Grid className="grid-cols-1 md:grid-cols-2 gap-4">
          <InputWithLabel
            id="firstName"
            label={t('common:personForm.firstName')}
            required
            placeholder={t('yourDetails.fields.firstName.placeholder')}
            value={formValues.firstName || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('firstName', e.target.value)
            }
            error={!!errors.firstName}
            errorMessage={errors.firstName?.message}
            testID="onboarding-first-name"
          />
          <InputWithLabel
            id="lastName"
            label={t('common:personForm.lastName')}
            required
            placeholder={t('yourDetails.fields.lastName.placeholder')}
            value={formValues.lastName || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('lastName', e.target.value)
            }
            error={!!errors.lastName}
            errorMessage={errors.lastName?.message}
            testID="onboarding-last-name"
          />
        </Grid>

        <Grid className="grid-cols-1 md:grid-cols-2 gap-4">
          <Dropdown
            id="salutation"
            label={t('common:personForm.prefix')}
            placeholder={t('yourDetails.fields.salutation.placeholder')}
            testID="onboarding-prefix"
            options={namePrefixes}
            value={formValues.salutation || ''}
            onChange={(value: string) => handleFieldChange('salutation', value)}
            error={!!errors.salutation}
            errorMessage={errors.salutation?.message}
            showLeftIcon={false}
          />
          <InputWithLabel
            id="jobTitle"
            required
            label={t('common:personForm.jobTitle')}
            placeholder={t('yourDetails.fields.jobTitle.placeholder')}
            value={formValues.jobTitle || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('jobTitle', e.target.value)
            }
            error={!!errors.jobTitle}
            errorMessage={errors.jobTitle?.message}
            testID="onboarding-job-title"
          />
        </Grid>

        <Grid className="grid-cols-1 md:grid-cols-2 gap-4">
          <InputWithLabel
            id="email"
            label={t('common:personForm.email')}
            type="email"
            required
            placeholder={t('yourDetails.fields.email.placeholder')}
            value={formValues.email || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('email', e.target.value)
            }
            error={!!errors.email}
            errorMessage={errors.email?.message}
            testID="onboarding-email"
          />

          <MobileInputField
            label={t('common:personForm.mobilePhone')}
            countries={countries}
            selectedCountry={formValues.country || DEFAULT_COUNTRY_CODE}
            onCountryChange={(val: string) => handleFieldChange('country', val)}
            phone={formValues.mobile || ''}
            onPhoneChange={(value: string) =>
              handleFieldChange('mobile', value)
            }
            onPhoneBlur={() => trigger(['mobile', 'country', 'phoneNumber'])}
            placeholder={t('yourDetails.fields.mobile.placeholder')}
            size="sm"
            required
            error={!!errors.mobile || !!errors.country}
            errorMessage={
              errors.mobile?.message || errors.country?.message || ''
            }
            testID="onboarding-mobile"
            countryDropdownDisabled={companyType !== 'INTERNATIONAL'}
          />
        </Grid>
      </form>
    </div>
  );
}
